import { useState, useEffect, useRef } from "react";
import { useProducts } from "../../hooks/useProducts";
import { useInventoryStats } from "../../hooks/useInventoryStats";
import { ProductService } from "../../services/productService";
import { ConfirmationDialog } from "../admin/ConfirmationDialog";
import { InventoryHeader } from "./inventory/InventoryHeader";
import { InventoryStats } from "./inventory/InventoryStats";
import { InventoryFilters } from "./inventory/InventoryFilters";
import { ProductList } from "./inventory/ProductList";
import { InventoryPagination } from "./inventory/InventoryPagination";

interface InventoryQueryParams {
  page: number;
  limit: number;
  search: string;
}

const ResponsiveAdminInventory: React.FC = () => {
  // State for query parameters - initialized from sessionStorage if available
  const [queryParams, setQueryParams] = useState<InventoryQueryParams>(() => {
    const savedState = sessionStorage.getItem("admin_inventory_state");
    if (savedState) {
      try {
        return JSON.parse(savedState) as InventoryQueryParams;
      } catch (e) {
        console.error("Failed to parse saved inventory state:", e);
      }
    }
    return {
      page: 1,
      limit: 20,
      search: "",
    };
  });

  // Debounced search state - initialized from queryParams to avoid initial delay
  const [debouncedSearch, setDebouncedSearch] = useState(queryParams.search);

  // UI state
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const isFirstRender = useRef(true);

  // Persist state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("admin_inventory_state", JSON.stringify(queryParams));
  }, [queryParams]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(queryParams.search.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [queryParams.search]);

  // Update page when debounced search changes, but not on initial render
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Only reset page if the search term actually changed
    setQueryParams(prev => ({ ...prev, page: 1 }));
  }, [debouncedSearch]);

  const { products, total, totalPages, isLoading, error, refetch } = useProducts(
    queryParams.page,
    queryParams.limit,
    debouncedSearch
  );

  const { stats, isLoading: isStatsLoading, refetch: refetchStats } = useInventoryStats();

  const handleDeleteProduct = async (): Promise<void> => {
    if (!deletingProductId) return;

    try {
      await ProductService.deleteProductById(deletingProductId);
      refetch();
      refetchStats(); // inventory changed — recompute the summary cards
      setDeletingProductId(null);
    } catch (error) {
      console.error("Failed to delete product:", error);
      setErrorMessage("Unable to delete product. Please try again later.");
      setShowErrorToast(true);
    }
  };

  const confirmDeleteProduct = (productId: string): void => {
    setDeletingProductId(productId);
    setShowDialog(true);
  };

  const handleConfirm = (): void => {
    if (deletingProductId) {
      handleDeleteProduct();
    }
    setShowDialog(false);
  };

  const handleCancel = (): void => {
    setDeletingProductId(null);
    setShowDialog(false);
  };

  const handleSearchChange = (query: string) => {
    setQueryParams(prev => ({ ...prev, search: query }));
  };

  const handleClearSearch = () => {
    setQueryParams(prev => ({ ...prev, search: "", page: 1 }));
    setDebouncedSearch("");
  };

  const handlePageChange = (page: number) => {
    setQueryParams(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (size: number) => {
    setQueryParams(prev => ({ ...prev, limit: size, page: 1 }));
  };


  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <InventoryHeader totalProducts={total} />

        <InventoryStats stats={stats} isLoading={isStatsLoading} />

        <InventoryFilters
          searchQuery={queryParams.search}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
        />

        <ProductList
          products={products}
          isLoading={isLoading}
          error={error}
          searchQuery={debouncedSearch}
          onClearSearch={handleClearSearch}
          onDeleteProduct={confirmDeleteProduct}
        />

        <InventoryPagination
          currentPage={queryParams.page}
          totalPages={totalPages}
          totalProducts={total}
          pageSize={queryParams.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      <ConfirmationDialog
        message="Are you sure you want to delete this product? This action cannot be undone."
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isOpen={showDialog}
        title="Delete Product"
      />

      {showErrorToast && (
        <div className="fixed bottom-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md z-50">
          <div className="flex">
            <div className="py-1">
              <svg className="h-6 w-6 text-red-500 mr-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="font-bold">Error</p>
              <p className="text-sm">{errorMessage}</p>
            </div>
            <button
              onClick={() => setShowErrorToast(false)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponsiveAdminInventory;
